import React, { useEffect, useState } from 'react';
import { getAllEPapers, addEPaper, deleteEPaper } from '../../firebase/firestore';
import { uploadEPaper, deleteFile } from '../../firebase/storage';
import { formatDateTime } from '../../utils/dateHelpers';
import './ManageEPapers.css';

const MAX_FILE_SIZE_MB = 100;

export default function ManageEPapers() {
  const [epapers, setEpapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], totalPages: 1 });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    const data = await getAllEPapers();
    setEpapers(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const sizeMB = selected.size / 1024 / 1024;
    if (sizeMB > MAX_FILE_SIZE_MB) {
      setError(`File is ${sizeMB.toFixed(1)}MB. Maximum allowed size is ${MAX_FILE_SIZE_MB}MB.`);
      setFile(null);
      return;
    }

    setFile(selected);
    setError('');
    setSuccess('');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return setError('Please select a PDF or image file');
    if (!form.date) return setError('Please select a date');

    const exists = epapers.find(p => p.date === form.date);
    if (exists && !window.confirm(`An e-paper already exists for ${form.date}. Replace it?`)) return;

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const ext = file.name.split('.').pop().toLowerCase();
      const fileType = ext === 'pdf' ? 'pdf' : 'image';

      const fileURL = await uploadEPaper(file, form.date, (progress) => {
        setUploadProgress(progress);
      });

      if (exists) await deleteEPaper(exists.id);

      await addEPaper({
        date: form.date,
        fileType,
        fileURL,
        pageURLs: fileType === 'image' ? [fileURL] : [],
        totalPages: fileType === 'pdf' ? parseInt(form.totalPages) || 1 : 1,
      });

      setSuccess(`E-Paper for ${form.date} uploaded successfully!`);
      setFile(null);
      setUploadProgress(0);
      setForm({ date: new Date().toISOString().split('T')[0], totalPages: 1 });
      load();
    } catch (err) {
      setError(`Upload failed: ${err.message || 'Please try again.'}`);
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (epaper) => {
    if (!window.confirm(`Delete e-paper for ${epaper.date}?`)) return;
    if (epaper.fileURL) await deleteFile(epaper.fileURL).catch(() => {});
    await deleteEPaper(epaper.id);
    setEpapers(prev => prev.filter(p => p.id !== epaper.id));
  };

  return (
    <div className="manage-epapers">
      <div className="page-header-row">
        <h1 className="page-title">📄 E-Paper Management</h1>
      </div>

      <div className="epaper-grid">
        <div className="upload-card">
          <h2 className="card-section-title">Upload New E-Paper</h2>
          {error && <div className="form-error">{error}</div>}
          {success && <div className="form-success">{success}</div>}
          <form onSubmit={handleUpload}>
            <div className="form-group">
              <label>Publication Date *</label>
              <input type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>E-Paper File (PDF or Image) *</label>
              <div className="file-dropzone" onClick={() => document.getElementById('epaper-file').click()}>
                {file ? (
                  <div className="file-selected">
                    <span className="file-icon">📄</span>
                    <div>
                      <div className="file-name">{file.name}</div>
                      <div className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                    <button type="button" className="remove-file" onClick={e => { e.stopPropagation(); setFile(null); }}>✕</button>
                  </div>
                ) : (
                  <div className="dropzone-prompt">
                    <span className="dropzone-icon">📤</span>
                    <p>Click to select PDF or image</p>
                    <span className="dropzone-hint">PDF, JPG, PNG · Max {MAX_FILE_SIZE_MB}MB</span>
                  </div>
                )}
              </div>
              <input id="epaper-file" type="file" accept=".pdf,image/*" style={{ display: 'none' }}
                onChange={handleFileChange} />
            </div>
            <div className="form-group">
              <label>Total Pages (for PDF)</label>
              <input type="number" min="1" max="100" value={form.totalPages}
                onChange={e => setForm(f => ({ ...f, totalPages: e.target.value }))} />
              <small className="form-hint">Used to enable page navigation in the viewer</small>
            </div>

            {uploading && (
              <div className="upload-progress-wrapper" style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                  <span>Uploading…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div style={{ background: '#e0e0e0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${uploadProgress}%`,
                    background: '#c0392b',
                    height: '100%',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={uploading}>
              {uploading ? `⏳ Uploading… ${uploadProgress}%` : '📤 Upload E-Paper'}
            </button>
          </form>
        </div>

        <div className="epapers-list-card">
          <h2 className="card-section-title">All E-Papers ({epapers.length})</h2>
          {loading ? <div className="loading-spinner">Loading...</div>
            : epapers.length === 0
              ? <div className="empty-state"><h3>No e-papers yet</h3></div>
              : (
                <div className="epapers-list">
                  {epapers.map(ep => (
                    <div key={ep.id} className="epaper-item">
                      <div className="epaper-item-icon">
                        {ep.fileType === 'pdf' ? '📕' : '🖼️'}
                      </div>
                      <div className="epaper-item-info">
                        <div className="epaper-item-date">📅 {ep.date}</div>
                        <div className="epaper-item-meta">
                          <span>{ep.fileType?.toUpperCase()}</span>
                          {ep.totalPages > 1 && <span>{ep.totalPages} pages</span>}
                          <span>👁 {ep.viewCount || 0} views</span>
                          <span>Uploaded: {formatDateTime(ep.uploadedAt)}</span>
                        </div>
                      </div>
                      <div className="epaper-item-actions">
                        <a href={ep.fileURL} target="_blank" rel="noopener noreferrer" className="btn btn-dark btn-sm">View</a>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(ep)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
          }
        </div>
      </div>
    </div>
  );
}
